import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Heart, MessageCircle, Share2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

export default function ArticleDetail() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadArticle();
    loadComments();
    checkFavorite();
    // Fire-and-forget view counter (once per mount)
    api.post(`/articles/${articleId}/view`).catch(() => {});
  }, [articleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadArticle = async () => {
    try {
      const { data } = await api.get(`/articles/${articleId}`);
      setArticle(data);
    } catch (error) {
      console.error('Failed to load article:', error);
    }
  };

  const loadComments = async () => {
    try {
      const { data } = await api.get(`/articles/${articleId}/comments`);
      setComments(data);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const checkFavorite = async () => {
    if (!user) return;
    setIsFavorite(user.favorites?.includes(articleId));
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Please login to save articles');
      return;
    }
    try {
      if (isFavorite) {
        await api.delete(`/favorites/${articleId}`);
        toast.success('Removed from favorites');
      } else {
        await api.post(`/favorites/${articleId}`);
        toast.success('Added to favorites');
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to comment');
      return;
    }
    try {
      await api.post(`/articles/${articleId}/comments`, {
        content: newComment,
        is_anonymous: isAnonymous
      });
      setNewComment('');
      toast.success('Comment added');
      loadComments();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  if (!article) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-16">
        <article className="p-8 rounded-3xl mb-12" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
          <div className="flex items-center justify-between mb-6">
            <span className="px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#E8C26520', color: '#E8C265', fontFamily: 'Nunito, sans-serif' }}>
              {article.category}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  const url = window.location.href;
                  const shareData = { title: article.title, text: `${article.title} — Love Restored`, url };
                  if (navigator.share) {
                    try { await navigator.share(shareData); } catch {}
                  } else {
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success('Link copied — share it with anyone.');
                    } catch { toast.error(`Copy this: ${url}`); }
                  }
                }}
                className="p-3 rounded-full transition-all duration-300 hover:scale-110"
                style={{ backgroundColor: '#F3EFE7' }}
                aria-label="Share this article"
                title="Share this article"
                data-testid="share-button"
              >
                <Share2 className="w-5 h-5" style={{ color: '#78A18E', strokeWidth: 2 }} />
              </button>
              <button
                onClick={toggleFavorite}
                className="p-3 rounded-full transition-all duration-300 hover:scale-110"
                style={{ backgroundColor: isFavorite ? '#E57A4420' : '#F3EFE7' }}
                data-testid="favorite-button"
              >
                <Heart className="w-5 h-5" style={{ color: '#E57A44', fill: isFavorite ? '#E57A44' : 'none', strokeWidth: 2 }} />
              </button>
            </div>
          </div>

          <h1 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            {article.title}
          </h1>

          <div className="prose prose-lg" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: '2' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content, { USE_PROFILES: { html: true } }) }}>
          </div>

          {article.scriptures && article.scriptures.length > 0 && (
            <div className="mt-8 p-6 rounded-2xl" style={{ backgroundColor: '#5B7C9910', borderLeft: '4px solid #5B7C99' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                Scripture References
              </h3>
              {article.scriptures.map((scripture, i) => (
                <blockquote key={i} className="mb-4 italic" style={{ fontFamily: 'Lora, serif', color: '#5B7C99' }}>
                  "{scripture.text}" - {scripture.reference}
                </blockquote>
              ))}
            </div>
          )}
        </article>

        <div className="p-8 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}>
          <div className="flex items-center gap-3 mb-6">
            <MessageCircle className="w-6 h-6" style={{ color: '#E57A44' }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              Comments ({comments.length})
            </h2>
          </div>

          {user && (
            <form onSubmit={handleCommentSubmit} className="mb-8">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                required
                rows={4}
                className="w-full px-4 py-3 rounded-xl border mb-4"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2A29' }}
                placeholder="Share your thoughts..."
                data-testid="comment-input"
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', fontSize: '14px' }}>Post anonymously</span>
                </label>
                <button
                  type="submit"
                  className="px-6 py-2 rounded-full transition-all duration-300 hover:-translate-y-1"
                  style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                  data-testid="submit-comment"
                >
                  Post Comment
                </button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="p-4 rounded-2xl" style={{ backgroundColor: '#FDFBF7' }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>
                    {comment.user_name}
                  </span>
                  <span style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D', fontSize: '12px' }}>
                    {new Date(comment.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{comment.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
